import { Link } from "@tanstack/react-router";
import type { ComponentProps } from "react";

import type { OrderEntity } from "~/entities/order";
import { RetrieveFrom } from "~/pages/order-printout/@x/home";
import { formatDateTime, Tk, useTranslation2 } from "~/shared/i18n";
import { COMMA_WITH_SPACE, SPACE } from "~/shared/str";
import { Button } from "~/shared/ui/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "~/shared/ui/card";

type Props = ComponentProps<typeof Card> & {
  order: OrderEntity.Schema;
  onDelete?: (() => void) | undefined;
};

function OrderCard({ order, onDelete, ...props }: Props) {
  const {
    translation: {
      i18n: { language },
    },
    postproc: { tc },
  } = useTranslation2();

  const { id, number, from, to, createdAt } = order;

  return (
    <Card {...props}>
      <CardHeader className="xs:flex-row flex flex-col justify-between">
        <CardTitle>{[tc(Tk.ORDER), number].join(SPACE)}</CardTitle>
        <CardAction className="max-xs:self-end flex flex-col gap-2 sm:flex-row">
          <Button asChild>
            <Link
              to={"/{-$locale}/order-printout/$orderId"}
              params={{
                orderId: id,
              }}
              search={{
                retrieveFrom: RetrieveFrom.LOCAL_STORAGE,
              }}
            >
              {tc(Tk.VIEW)}
            </Link>
          </Button>
          <Button variant="destructive" onClick={onDelete}>
            {tc(Tk.DELETE)}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <ul>
          {[
            {
              labelT: Tk.FROM_WHERE,
              value: [from.city, from.address].join(COMMA_WITH_SPACE),
            },
            {
              labelT: Tk.TO,
              value: [to.city, to.address].join(COMMA_WITH_SPACE),
            },
            {
              labelT: Tk.CREATION_DATE,
              value: formatDateTime(language, createdAt),
            },
          ].map(({ labelT, value }, index) => (
            <li key={index} className="flex justify-between">
              <span>{tc(labelT)}</span>
              <span className="font-semibold">{value}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export { OrderCard };
